import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createInviteSchema, type Location, type Member } from '@wrapforce/shared';
import { apiErrorMessage } from '@/lib/api-errors';

type InviteFormValues = z.input<typeof createInviteSchema>;
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Select, SelectItem } from '@/components/ui/select';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useSession } from '@/features/session/useSession';
import { useLocaleFormatters } from '@/lib/format';
import { AuditLogCard } from '@/features/audit/AuditLogCard';
import { useLocations, useUpdateMemberLocations } from '@/features/locations/api';
import { ShopsSection } from '@/features/locations/LocationsSettings';
import {
  useWorkspaceDetail, useMembers, useInvites, useCreateInvite, useRevokeInvite,
  useUpdateMemberRole, useSetMemberActive, useRemoveMember, useTransferOwnership, useDeleteWorkspace,
} from './api';

export function WorkspacePage() {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const { formatDate } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const isOwner = membership?.role === 'owner';
  const isManager = membership?.role === 'manager';

  const { data: detail } = useWorkspaceDetail(workspaceId);
  const { data: members = [] } = useMembers(workspaceId);
  const { data: invites = [] } = useInvites(workspaceId);
  const { data: locationsData } = useLocations(workspaceId);

  const [inviteOpen, setInviteOpen] = useState(false);

  if (!workspaceId || !detail) return null;
  const seatLimit = PLAN_DEFINITIONS[detail.plan].seats;
  const activeLocations = (locationsData?.items ?? []).filter((l) => l.isActive);
  const multiLocationOn = PLAN_DEFINITIONS[detail.plan].gates.multiLocation;
  const showLocationsColumn = isOwner && multiLocationOn && activeLocations.length >= 2;
  const showShopsSection = (isOwner || isManager) && multiLocationOn;

  return (
    <div className="flex flex-col gap-4 p-6">
      <Card>
        <CardContent className="flex items-center gap-4">
          <div className="grid h-12 w-12 flex-none place-items-center rounded-[var(--radius-card)] bg-accent text-lg font-bold text-on-accent">
            {detail.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display text-base font-bold text-ink">{detail.name}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-muted">
              <span>
                @{detail.slug} · {t('shop.created', { date: formatDate(new Date(detail.createdAt), 'P') })} ·{' '}
                <span className="font-semibold text-accent-ink">{t('shop.planLabel', { plan: t(`plan.${detail.plan}.label`, { ns: 'settings' }) })}</span>
              </span>
              {isOwner ? (
                <Button size="sm" variant="outline" onClick={() => navigate('/settings?tab=billing')}>
                  {t('billing.changePlan', { ns: 'settings' })}
                </Button>
              ) : null}
            </div>
          </div>
          <div className="ml-auto flex flex-none flex-col items-end gap-0.5 text-right text-[12.5px] text-muted">
            <span>{t('shop.seatsUsed', { count: detail.seatCount, limit: seatLimit ?? '∞' })}</span>
            <span>{t('shop.aiCreditsLeft', { count: detail.aiCreditsBalance })}</span>
          </div>
        </CardContent>
      </Card>

      {showShopsSection ? <ShopsSection workspaceId={workspaceId} isOwner={isOwner} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('team.title', { count: members.length })}</CardTitle>
          {isOwner ? (
            <Button size="sm" className="ml-auto" onClick={() => setInviteOpen(true)}>
              {t('team.inviteMember')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-0 p-0">
          {members.map((m) => (
            <MemberRow
              key={m.userId}
              member={m}
              isOwner={isOwner}
              workspaceId={workspaceId}
              showLocations={showLocationsColumn}
              locations={activeLocations}
            />
          ))}
          {members.length === 0 ? <p className="p-4 text-sm text-muted">{t('team.noMembers')}</p> : null}
        </CardContent>
      </Card>

      {isOwner && invites.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('invites.pendingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-0 p-0">
            {invites.map((inv) => (
              <InviteRow key={inv.id} invite={inv} workspaceId={workspaceId} />
            ))}
          </CardContent>
        </Card>
      ) : null}

      <AuditLogCard workspaceId={workspaceId} role={membership?.role ?? ''} plan={detail.plan} />

      {isOwner ? <DangerZone workspaceId={workspaceId} workspaceName={detail.name} members={members} /> : null}

      {isOwner ? <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} workspaceId={workspaceId} /> : null}
    </div>
  );
}

function MemberRow({
  member, isOwner, workspaceId, showLocations, locations,
}: {
  member: Member;
  isOwner: boolean;
  workspaceId: string;
  showLocations: boolean;
  locations: Location[];
}) {
  const { t } = useTranslation('workspace');
  const updateRole = useUpdateMemberRole(workspaceId);
  const setActive = useSetMemberActive(workspaceId);
  const removeMember = useRemoveMember(workspaceId);
  const canManage = isOwner && member.role !== 'owner' && !member.isSelf;
  const { confirm, ConfirmDialog } = useConfirm();

  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <ConfirmDialog />
      <div className="grid h-8 w-8 flex-none place-items-center bg-ink text-[11.5px] font-semibold text-surface">
        {(member.fullName || member.email || '??').slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-ink">{member.fullName || member.email}{member.isSelf ? ` ${t('team.youSuffix')}` : ''}</div>
        <div className="text-xs text-muted">{member.email}</div>
      </div>
      <span
        className={`px-2.5 py-1 text-[10.5px] font-bold tracking-wide ${
          member.isActive ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
        }`}
      >
        {member.isActive ? t('team.active') : t('team.inactive')}
      </span>
      {showLocations ? (
        member.role === 'owner' ? (
          <span className="w-32 flex-none text-right text-[12.5px] text-muted">{t('memberLocations.allLocations')}</span>
        ) : (
          <MemberLocationsCell member={member} workspaceId={workspaceId} locations={locations} />
        )
      ) : null}
      {canManage ? (
        <Select value={member.role} onValueChange={(role) => updateRole.mutate({ userId: member.userId, role })}>
          <SelectItem value="manager">{t('roles.manager')}</SelectItem>
          <SelectItem value="front_desk">{t('roles.front_desk')}</SelectItem>
          <SelectItem value="installer">{t('roles.installer')}</SelectItem>
        </Select>
      ) : (
        <span className="w-24 text-right text-[12.5px] font-medium text-muted">{t(`roles.${member.role}`)}</span>
      )}
      {canManage ? (
        <>
          <Button variant="ghost" size="sm" onClick={() => setActive.mutate({ userId: member.userId, active: !member.isActive })}>
            {member.isActive ? t('team.deactivate') : t('team.reactivate')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger hover:text-danger"
            onClick={async () => {
              const ok = await confirm({
                title: t('team.removeConfirmTitle', { name: member.fullName || member.email }),
                description: t('team.removeConfirmDescription'),
                confirmLabel: t('team.remove'),
                variant: 'danger',
              });
              if (ok) removeMember.mutate(member.userId);
            }}
          >
            {t('team.remove')}
          </Button>
        </>
      ) : null}
    </div>
  );
}

function MemberLocationsCell({
  member, workspaceId, locations,
}: {
  member: Member;
  workspaceId: string;
  locations: Location[];
}) {
  const { t } = useTranslation('workspace');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(member.locationIds);
  const updateMemberLocations = useUpdateMemberLocations(workspaceId);

  useEffect(() => {
    setSelected(member.locationIds);
  }, [member.locationIds]);

  const summary = member.locationIds.length === 0
    ? t('memberLocations.allLocations')
    : t('memberLocations.countLocations', { count: member.locationIds.length });

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSave() {
    try {
      await updateMemberLocations.mutateAsync({ userId: member.userId, input: { locationIds: selected } });
      toast.success(t('toast.memberLocationsUpdated'));
      setOpen(false);
    } catch (err) {
      toast.error(t('memberLocations.saveError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setSelected(member.locationIds);
      }}
    >
      <Popover.Trigger asChild>
        <button type="button" className="w-32 flex-none text-right text-[12.5px] font-medium text-accent-ink hover:underline">
          {summary}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-64 border border-border bg-surface p-3 shadow-md">
          <p className="mb-2 text-xs text-muted">{t('memberLocations.helperText')}</p>
          <div className="flex flex-col gap-1.5">
            {locations.map((loc) => (
              <label key={loc.id} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={selected.includes(loc.id)}
                  onChange={() => toggle(loc.id)}
                  className="h-4 w-4 accent-[var(--color-accent)]"
                />
                {loc.name}
              </label>
            ))}
          </div>
          <Button size="sm" className="mt-3 w-full" loading={updateMemberLocations.isPending} onClick={handleSave}>
            {t('common:actions.save')}
          </Button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function InviteRow({ invite, workspaceId }: { invite: { id: string; email: string; role: string; expiresAt: string }; workspaceId: string }) {
  const { t } = useTranslation('workspace');
  const { formatDate } = useLocaleFormatters();
  const revoke = useRevokeInvite(workspaceId);
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-ink">{invite.email}</div>
        <div className="text-xs text-muted">{t('invites.expires', { date: formatDate(new Date(invite.expiresAt), 'P') })}</div>
      </div>
      <span className="text-[12.5px] font-medium text-muted">{t(`roles.${invite.role}`)}</span>
      <Button variant="ghost" size="sm" className="text-danger hover:text-danger" onClick={() => revoke.mutate(invite.id)}>
        {t('invites.revoke')}
      </Button>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, workspaceId }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string }) {
  const { t } = useTranslation('workspace');
  const createInvite = useCreateInvite(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({ resolver: zodResolver(createInviteSchema), defaultValues: { role: 'installer' } });

  async function onSubmit(values: InviteFormValues) {
    await createInvite.mutateAsync(createInviteSchema.parse(values));
    toast.success(t('toast.inviteSent', { email: values.email }));
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('team.inviteMember')} description={t('invites.dialogDescription')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-email">{t('invites.emailLabel')}</Label>
          <Input id="invite-email" type="email" placeholder={t('invites.emailPlaceholder')} {...register('email')} />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('invites.roleLabel')}</Label>
          <select {...register('role')} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
            <option value="installer">{t('roles.installer')}</option>
            <option value="front_desk">{t('roles.front_desk')}</option>
            <option value="manager">{t('roles.manager')}</option>
          </select>
        </div>
        <Button type="submit" loading={createInvite.isPending} className="mt-1 w-full">
          {t('invites.submit')}
        </Button>
      </form>
    </Dialog>
  );
}

function DangerZone({ workspaceId, workspaceName, members }: { workspaceId: string; workspaceName: string; members: Member[] }) {
  const { t } = useTranslation('workspace');
  const { session } = useSession();
  const navigate = useNavigate();
  const transferOwnership = useTransferOwnership(workspaceId);
  const deleteWorkspace = useDeleteWorkspace(workspaceId);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const eligibleTargets = members.filter((m) => m.role !== 'owner');

  return (
    <Card className="border-danger/30">
      <CardHeader>
        <CardTitle className="text-danger">{t('dangerZone.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-ink">{t('dangerZone.transferTitle')}</div>
            <p className="text-xs text-muted">{t('dangerZone.transferDescription')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)} disabled={eligibleTargets.length === 0}>
            {t('dangerZone.transferCta')}
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-border pt-3">
          <div>
            <div className="text-sm font-semibold text-ink">{t('dangerZone.deleteTitle')}</div>
            <p className="text-xs text-muted">{t('dangerZone.deleteDescription')}</p>
          </div>
          <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
            {t('common:actions.delete')}
          </Button>
        </div>
      </CardContent>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen} title={t('dangerZone.transferTitle')} description={t('dangerZone.transferDialogDescription')}>
        <div className="flex flex-col gap-4">
          <Select value={transferTarget} onValueChange={setTransferTarget}>
            {eligibleTargets.map((m) => (
              <SelectItem key={m.userId} value={m.userId}>
                {m.fullName || m.email}
              </SelectItem>
            ))}
          </Select>
          <Button
            variant="danger"
            disabled={!transferTarget}
            loading={transferOwnership.isPending}
            onClick={async () => {
              await transferOwnership.mutateAsync({ toUserId: transferTarget });
              toast.success(t('toast.ownershipTransferred'));
              setTransferOpen(false);
            }}
          >
            {t('dangerZone.confirmTransferCta')}
          </Button>
        </div>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen} title={t('dangerZone.deleteTitle')} description={t('dangerZone.deleteConfirmPrompt', { name: workspaceName })}>
        <div className="flex flex-col gap-4">
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={workspaceName} />
          <Button
            variant="danger"
            disabled={confirmName !== workspaceName}
            loading={deleteWorkspace.isPending}
            onClick={async () => {
              await deleteWorkspace.mutateAsync(confirmName);
              if (session?.user.id) localStorage.removeItem(`wf-active-workspace:${session.user.id}`);
              navigate('/onboarding', { replace: true });
            }}
          >
            {t('dangerZone.permanentlyDeleteCta')}
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
